/**
 * Access, as the backend reports it (GET /users/permissions).
 *
 * The two halves of the grant registry, named as the backend names them:
 *
 * **Resources** are tables — items, clients, bills of materials. They carry
 * per-action, per-column rules, plus capabilities that are not columns: an
 * item's photos, barcodes and aliases, a client's wallet, and the created /
 * updated columns of every table.
 *
 * **Postings** are the acts — recording a sale, a supply, a payment; and, since
 * they only ever read, the statements too: the dashboard, the situations, stock
 * tracing. They carry the four verbs, the fields of the document, and the
 * modifiers that narrow one verb: back-dating, printing a receipt, removing a
 * line, tendering from a wallet.
 *
 * Whether a user may post *at all* is a grant now, not a route permission, so
 * this feed answers that too.
 *
 * The backend enforces all of it. These helpers exist so the UI does not offer a
 * column, an input or a button that would come back empty or refused.
 *
 * **Nothing is permitted there until it is granted**, but the feed is still a
 * list of what is *closed*: the server resolves the grants and reports the
 * result, which is smaller than the grant table and does not change when the
 * way grants cascade does. So a resource the feed does not mention is one the
 * user holds in full, and a resource with nothing granted arrives with every
 * action closed and every column hidden.
 */

import { MRT_ColumnDef } from "material-react-table";

export type ResourceAction = "read" | "create" | "update" | "delete";

export interface ResourceRules {
	/** Actions that are closed outright, e.g. { delete: false }. */
	actions: Record<string, boolean>;
	/** Columns that may not be read. */
	hidden: string[];
	/** Payload keys carrying those columns, including API aliases. */
	hiddenKeys: string[];
	/** Readable but not updatable. */
	readonly: string[];
	/** Not settable when creating. */
	noCreate: string[];
	/** Every capability this resource owns, restricted or not. */
	capabilityNames: string[];
	/** Capability name -> the actions denied on it. */
	capabilities: Record<string, string[]>;
}

export type PostingAction = "read" | "create" | "update" | "delete";

export interface PostingRules {
	/** Verbs that are closed outright, e.g. { delete: false }. */
	actions: Record<string, boolean>;
	/** Fields of the document that may not be seen. */
	hidden: string[];
	/** Visible, but the server supplies the value. */
	readonly: string[];
	/** Payload keys carrying the hidden fields. */
	hiddenKeys: string[];
	/** Verb -> the modifiers denied under it (back-dating, bulk delete, …). */
	modifiers: Record<string, string[]>;
}

export interface AccessPolicy {
	resources: Record<string, ResourceRules>;
	postings: Record<string, PostingRules>;
}

export const EMPTY_ACCESS: AccessPolicy = { resources: {}, postings: {} };

const list = (value: unknown): string[] =>
	Array.isArray(value) ? value.map((item) => String(item)) : [];

const normalizeResource = (raw: any): ResourceRules => ({
	actions: raw && typeof raw.actions === "object" && raw.actions ? { ...raw.actions } : {},
	hidden: list(raw?.hidden),
	hiddenKeys: list(raw?.hidden_keys ?? raw?.hiddenKeys),
	readonly: list(raw?.readonly),
	noCreate: list(raw?.no_create ?? raw?.noCreate),
	capabilityNames: list(raw?.capability_names ?? raw?.capabilityNames),
	capabilities:
		raw && typeof raw.capabilities === "object" && raw.capabilities
			? Object.fromEntries(
				Object.entries(raw.capabilities).map(([name, actions]) => [name, list(actions)]),
			)
			: {},
});

const normalizePosting = (raw: any): PostingRules => ({
	actions: raw && typeof raw.actions === "object" && raw.actions ? { ...raw.actions } : {},
	hidden: list(raw?.hidden),
	readonly: list(raw?.readonly),
	hiddenKeys: list(raw?.hidden_keys ?? raw?.hiddenKeys),
	modifiers:
		raw && typeof raw.modifiers === "object" && raw.modifiers
			? Object.fromEntries(
				Object.entries(raw.modifiers).map(([action, names]) => [action, list(names)]),
			)
			: {},
});

export const normalizeAccess = (rawResources: any, rawPostings: any): AccessPolicy => {
	const resources: Record<string, ResourceRules> = {};
	if (rawResources && typeof rawResources === "object") {
		for (const [name, rules] of Object.entries(rawResources)) {
			resources[name] = normalizeResource(rules);
		}
	}
	const postings: Record<string, PostingRules> = {};
	if (rawPostings && typeof rawPostings === "object") {
		for (const [name, rules] of Object.entries(rawPostings)) {
			postings[name] = normalizePosting(rules);
		}
	}
	return { resources, postings };
};

/** Stable signature, so a re-fetch that changed nothing does not re-render. */
export const accessFingerprint = (policy: AccessPolicy | null | undefined): string => {
	if (!policy) return "";
	const parts: string[] = [];
	for (const [name, rules] of Object.entries(policy.resources).sort()) {
		parts.push(
			`r:${name}:${Object.entries(rules.actions).map(([action, allowed]) => `${action}=${allowed}`).sort().join(",")}` +
			`:${[...rules.hidden].sort().join(",")}:${[...rules.hiddenKeys].sort().join(",")}` +
			`:${[...rules.readonly].sort().join(",")}:${[...rules.noCreate].sort().join(",")}` +
			`:${[...rules.capabilityNames].sort().join(",")}` +
			`:${Object.entries(rules.capabilities).map(([cap, acts]) => `${cap}=${[...acts].sort().join("/")}`).sort().join(",")}`,
		);
	}
	for (const [name, rules] of Object.entries(policy.postings).sort()) {
		parts.push(
			`p:${name}:${Object.entries(rules.actions).map(([action, allowed]) => `${action}=${allowed}`).sort().join(",")}` +
			`:${[...rules.hidden].sort().join(",")}:${[...rules.hiddenKeys].sort().join(",")}` +
			`:${[...rules.readonly].sort().join(",")}` +
			`:${Object.entries(rules.modifiers).map(([action, names]) => `${action}=${[...names].sort().join("/")}`).sort().join(",")}`,
		);
	}
	return parts.join("|");
};

const rulesFor = (policy: AccessPolicy | null | undefined, resource: string): ResourceRules | null =>
	policy?.resources?.[resource] ?? null;

/**
 * May this user perform `action` on the resource — or, with a `target`, on one
 * of its columns or capabilities?
 */
export const can = (
	policy: AccessPolicy | null | undefined,
	resource: string,
	action: ResourceAction,
	target?: string,
): boolean => {
	const rules = rulesFor(policy, resource);
	if (!rules) return true;

	if (target && (rules.capabilityNames.includes(target) || target in rules.capabilities)) {
		// A capability is its own thing: refusing to delete items does not
		// refuse the right to remove an item's photo, and the row action does
		// not reach it.
		return !(rules.capabilities[target] ?? []).includes(action);
	}
	if (rules.actions[action] === false) return false;
	if (!target) return true;
	if (rules.hidden.includes(target)) return false;
	if (action === "update") return !rules.readonly.includes(target);
	if (action === "create") return !rules.noCreate.includes(target);
	return true;
};

export const canRead = (policy: AccessPolicy | null | undefined, resource: string, target?: string) =>
	can(policy, resource, "read", target);
export const canCreate = (policy: AccessPolicy | null | undefined, resource: string, target?: string) =>
	can(policy, resource, "create", target);
export const canUpdate = (policy: AccessPolicy | null | undefined, resource: string, target?: string) =>
	can(policy, resource, "update", target);
export const canDelete = (policy: AccessPolicy | null | undefined, resource: string, target?: string) =>
	can(policy, resource, "delete", target);

// ---------------------------------------------------------------- postings

/**
 * May this user perform `action` on the posting — or, with a `modifier`, take
 * that particular liberty while doing it?
 *
 * Mirrors `PostingPolicy.can` on the server. A modifier is never implied by the
 * verb it narrows: being allowed to record a sale is not being allowed to
 * back-date one, and being allowed to read a sale is not being allowed to print
 * its receipt. The feed lists a modifier under a verb only when it is *denied*,
 * and a closed verb lists all of its modifiers, so one check covers both.
 */
export const canPost = (
	policy: AccessPolicy | null | undefined,
	posting: string,
	action: PostingAction,
	modifier?: string,
): boolean => {
	const rules = policy?.postings?.[posting];
	if (!rules) return true;
	if (rules.actions[action] === false) return false;
	if (!modifier) return true;
	return !(rules.modifiers[action] ?? []).includes(modifier);
};

export const canRecord = (policy: AccessPolicy | null | undefined, posting: string, modifier?: string) =>
	canPost(policy, posting, "create", modifier);
export const canAmend = (policy: AccessPolicy | null | undefined, posting: string, modifier?: string) =>
	canPost(policy, posting, "update", modifier);
export const canSee = (policy: AccessPolicy | null | undefined, posting: string, modifier?: string) =>
	canPost(policy, posting, "read", modifier);
export const canDrop = (policy: AccessPolicy | null | undefined, posting: string, modifier?: string) =>
	canPost(policy, posting, "delete", modifier);

/** Any verb at all on this posting: "is this screen worth showing". */
export const canTouch = (policy: AccessPolicy | null | undefined, posting: string): boolean =>
	(["read", "create", "update", "delete"] as PostingAction[]).some((action) =>
		canPost(policy, posting, action),
	);

/** True when any of these postings is reachable at all. */
export const canSeeAny = (
	policy: AccessPolicy | null | undefined,
	postings: readonly string[],
): boolean => postings.some((posting) => canSee(policy, posting));

/** Fields of a posting: may they see it, may they set it. */
export const canViewPostingField = (
	policy: AccessPolicy | null | undefined,
	posting: string,
	field: string,
): boolean => !policy?.postings?.[posting]?.hidden?.includes(field);

export const canSetPostingField = (
	policy: AccessPolicy | null | undefined,
	posting: string,
	field: string,
): boolean => {
	const rules = policy?.postings?.[posting];
	if (!rules) return true;
	return !rules.hidden.includes(field) && !rules.readonly.includes(field);
};

/** Older names, kept so call sites can move one at a time. */
export const canViewOperationField = canViewPostingField;
export const canSetOperationField = canSetPostingField;

/**
 * The created / updated columns, which are a sub of each table and each document
 * rather than one flag for all of them. This was `can_view_timestamps`.
 *
 * The server strips both keys from the response when the sub is not granted, so
 * this is only about not offering a column that would arrive empty.
 */
export const canSeeTimestamps = (
	policy: AccessPolicy | null | undefined,
	scope: { resource?: string; posting?: string },
): boolean => {
	if (scope.resource && !can(policy, scope.resource, "read", "timestamps")) return false;
	if (scope.posting && !canSee(policy, scope.posting, "timestamps")) return false;
	return true;
};

// ------------------------------------------------- the statements and screens
//
// Postings in the backend's "reports" section: they only ever read, and the sub
// says which view. Named here because these read better at a call site than
// canSee(access, "situation", "client") does, and because the UI asks for them
// in many places.

export const canSeeDashboard = (policy: AccessPolicy | null | undefined) =>
	canSee(policy, "dashboard");
export const canSeeAdvancedDashboard = (policy: AccessPolicy | null | undefined) =>
	canSee(policy, "dashboard", "advanced");
export const canSeeReports = (policy: AccessPolicy | null | undefined) =>
	canSee(policy, "report");
export const canSeeLiveStock = (policy: AccessPolicy | null | undefined) =>
	canSee(policy, "inventory_current");
export const canTraceStock = (policy: AccessPolicy | null | undefined) =>
	canSee(policy, "stock_tracing");
export const canSeeStockTimeline = (policy: AccessPolicy | null | undefined) =>
	canSee(policy, "stock_timeline");
export const canSeeWhoIsSignedIn = (policy: AccessPolicy | null | undefined) =>
	canSee(policy, "user_activity");
export const canSignOthersOut = (policy: AccessPolicy | null | undefined) =>
	canSee(policy, "user_activity", "force_logout");

export type SituationKind = "client" | "agent" | "invoice" | "supplier" | "supply";

/** One statement, by name: each kind is its own situation subgrant. */
export const canSeeSituation = (
	policy: AccessPolicy | null | undefined,
	kind: SituationKind,
) => canSee(policy, "situation", kind);

export const canSeeAnySituation = (
	policy: AccessPolicy | null | undefined,
	kinds: readonly SituationKind[] = ["client", "agent", "invoice", "supplier", "supply"],
) => kinds.some((kind) => canSeeSituation(policy, kind));

// --------------------------------------------------------- sale-side liberties

/** Printing a slip narrows reading the sale it belongs to. */
export const canPrintReceipt = (policy: AccessPolicy | null | undefined) =>
	canSee(policy, "sale", "print_receipt");

/** Paying with a client's wallet instead of cash, while ringing up or amending. */
export const canTenderFromWallet = (policy: AccessPolicy | null | undefined) =>
	canRecord(policy, "sale", "wallet_tender") || canAmend(policy, "sale", "wallet_tender");

/** Changing a line's quantity, or dropping a line, while amending a document. */
export const canChangeLineQty = (policy: AccessPolicy | null | undefined, posting = "sale") =>
	canAmend(policy, posting, "line_qty");
export const canRemoveLine = (policy: AccessPolicy | null | undefined, posting = "sale") =>
	canAmend(policy, posting, "line");

// --------------------------------------------------------------- grant specs
//
// The backend's ROUTE_PERMISSIONS is a tree of specs -- posting("sale","create"),
// resource("item","read"), any_of(...) -- resolved by one function. The screens,
// tabs and hub cards here are the same kind of table, so they carry the same kind
// of spec and one resolver reads it. Writing a check inline is still fine; this
// is for the places that are data rather than code.

export type GrantSpec =
	| { posting: string; action?: PostingAction; modifier?: string }
	| { resource: string; action?: ResourceAction; target?: string }
	| { anyOf: GrantSpec[] }
	| { allOf: GrantSpec[] }
	/** Any valid session. */
	| "any";

export type Principal = {
	access: AccessPolicy | null | undefined;
};

/** Does this principal satisfy the grant spec? Mirrors the backend resolver. */
export const allows = (principal: Principal, spec: GrantSpec): boolean => {
	if (spec === "any") return true;
	if ("anyOf" in spec) return spec.anyOf.some((inner) => allows(principal, inner));
	if ("allOf" in spec) return spec.allOf.every((inner) => allows(principal, inner));
	if ("posting" in spec) {
		return canPost(principal.access, spec.posting, spec.action ?? "read", spec.modifier);
	}
	return can(principal.access, spec.resource, spec.action ?? "read", spec.target);
};

/** Shorthands, so a table of specs stays readable. */
export const reads = (posting: string): GrantSpec => ({ posting, action: "read" });
export const records = (posting: string): GrantSpec => ({ posting, action: "create" });
export const situation = (kind: SituationKind): GrantSpec => ({
	posting: "situation",
	action: "read",
	modifier: kind,
});
export const anyOf = (...specs: GrantSpec[]): GrantSpec => ({ anyOf: specs });

/** Payload keys to drop from a table or an export, for one resource or posting. */
export const hiddenKeys = (
	policy: AccessPolicy | null | undefined,
	{ resource, posting, operation }: { resource?: string; posting?: string; operation?: string },
): string[] => {
	const name = posting ?? operation;
	return [
		...(resource ? policy?.resources?.[resource]?.hiddenKeys ?? [] : []),
		...(name ? policy?.postings?.[name]?.hiddenKeys ?? [] : []),
	];
};

/** Drop the columns this user may not read. */
export const filterColumns = <T extends MRT_ColumnDef<any>>(
	columns: T[],
	policy: AccessPolicy | null | undefined,
	scope: { resource?: string; posting?: string; operation?: string },
): T[] => {
	const keys = hiddenKeys(policy, scope);
	if (!keys.length) return columns;
	return columns.filter((column) => {
		const key = String((column as any).accessorKey ?? column.id ?? "");
		return !key || !keys.includes(key);
	});
};
